import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useSnackbar } from 'notistack';
import { Formik, Form } from 'formik';
import { mapValues, uniqBy } from 'lodash';
import * as Yup from 'yup';
import { Table, TableBody, TableHead, TableRow, TableCell, Button, Box, Paper } from '@mui/material';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { getUserInitials } from 'utils/user.util';
import { useDeviceCtx } from 'context/DeviceContext';
import { useCurrUserCtx } from 'context/CurrUserContext';
import radqcApi from 'dataExchange/radqcApiClient';
import ToggleableEntry from 'views/entry/components/ToggleableEntry';
import GeoCalibForm from './components/GeoCalibForm';
import GeoCalibDisplay from './components/GeoCalibDisplay';

const styles = (theme) => ({
  headerTableRow: { backgroundColor: 'transparent' },
  tableContainer: {
    boxShadow: 'none',
    height: '480px',
    overflowY: 'auto',
  },
  bottomNavigation: {
    height: '60px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    borderBottom: 'none',
    backgroundColor: `${theme.palette.primary.main}0a`,
  },
});

const generateEmptyGeoCalibEntry = (currUser, device) => ({
  _id: 'new',
  deviceId: device._id,
  date: new Date(),
  remarks: '',
  outFields: [],
  initials: getUserInitials(currUser),
  enteredById: currUser._id,
});

function GeoCalibTable(props) {
  const { match, classes, geoCalibEntries, mode, updateEntries } = props;
  const [entryKey, setEntryKey] = useState(0);
  const { getAccessTokenSilently } = useAuth0();
  const { devices } = useDeviceCtx();
  const { currUser } = useCurrUserCtx();
  const { enqueueSnackbar } = useSnackbar();
  const device = devices.find((a) => match.url.includes(a._id));

  const initialValues = { entries: {} };
  let newEntryForm = null;
  if (mode === 'enter') {
    initialValues.entries = { new: generateEmptyGeoCalibEntry(currUser, device) };
    newEntryForm = <GeoCalibForm entryData={initialValues.entries.new} mode={mode} />;
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object({
        entries: Yup.lazy((obj) =>
          Yup.object(
            mapValues(obj, () =>
              Yup.object({
                deviceId: Yup.string().required('Required'),
                date: Yup.string().required('Required'),
                remarks: Yup.string(),
                outFields: Yup.array(),
                initials: Yup.string().required('Required'),
                enteredById: Yup.string().required('Required'),
              }),
            ),
          ),
        ),
      })}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        const accessToken = await getAccessTokenSilently();
        const newEntries = Object.keys(values.entries).map((entry) => values.entries[entry]);
        console.log('newEntries:', newEntries);
        if (newEntries[0]._id === 'new') {
          const newEntry = newEntries[0];
          delete newEntry._id;
          await radqcApi('post', '/entries/mam_gc/', newEntry, { accessToken });
          enqueueSnackbar(`Added new Geo Calibration entry`, { variant: 'info' });
        } else {
          // TODO: add new back end endpoint to accept multiple modified entries at once
          await newEntries.forEach((entry) => radqcApi('put', `/entries/mam_gc/${entry._id}`, entry, { accessToken }));
          enqueueSnackbar(`Updated ${newEntries.length} entr${newEntries.length === 1 ? 'y' : 'ies'}`, {
            variant: 'info',
          });
        }
        // The following is used to force React to re-render the form
        setEntryKey(entryKey + 1);
        updateEntries();
        setSubmitting(false);
        resetForm();
      }}
    >
      {({ values }) => {
        let allGeoCalibEntries = geoCalibEntries;
        if (mode === 'edit') {
          const savedGeoCalibEntries = geoCalibEntries || [];
          allGeoCalibEntries = [...savedGeoCalibEntries, ...Object.values(values.entries)].sort(
            (a, b) => a.date - b.date,
          );
          allGeoCalibEntries = uniqBy(allGeoCalibEntries, '_id');
        }
        return (
          <Form>
            <Box component={Paper} className={classes.tableContainer}>
              <Table className={classes.table} aria-label="GeoCalibTable">
                <TableHead>
                  <TableRow>
                    <TableCell size="small">Date</TableCell>
                    <TableCell size="small">Initials</TableCell>
                    <TableCell size="small">Remarks</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {newEntryForm}
                  {allGeoCalibEntries.map((entry) => (
                    <ToggleableEntry
                      key={entryKey + entry.date}
                      entryData={entry}
                      mode={mode}
                      entryDisplay={GeoCalibDisplay}
                      entryForm={GeoCalibForm}
                      className={classes.entriesList}
                    />
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Table className={classes.bottomNavigation}>
              <TableHead className={classes.bottomTableHead}>
                <TableRow className={classes.headerTableRow}>
                  <TableCell colSpan={2} sx={{ border: 'none' }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      disabled={Object.keys(values.entries).length < 1}
                      type="submit"
                    >
                      Save Change
                    </Button>
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </Form>
        );
      }}
    </Formik>
  );
}

GeoCalibTable.propTypes = {
  geoCalibEntries: PropTypes.arrayOf(PropTypes.any),
  mode: PropTypes.string.isRequired,
  updateEntries: PropTypes.func.isRequired,
};

GeoCalibTable.defaultProps = {
  geoCalibEntries: [],
};

export default withRouter(withStyles(styles)(GeoCalibTable));
